import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatPrice } from "../../lib/format";
import axios from "axios";
import { api } from "../../lib/api";
import { toast } from "sonner";

const empty = {
  code: "",
  name: "",
  description: "",
  composition: "",
  price: 0,
  compare_at_price: null,
  sale_percent: 0,
  category_slug: "",
  sizes: [],
  stock_by_size: {},
  colors: [],
  images: [],
  is_new: false,
  is_favorite: false,
  is_active: true,
  related_product_ids: [],
};

const MAX_RELATED = 5;

const toDriveDirect = (url) => {
  const m = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (m) return `https://lh3.googleusercontent.com/d/${m[1]}=w1600`;
  const m2 = url.match(/[?&]id=([^&]+)/);
  if (m2 && url.includes("drive.google.com")) return `https://lh3.googleusercontent.com/d/${m2[1]}=w1600`;
  return url;
};

const MAX_UPLOAD_MB = 10;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif", "image/gif"];

async function uploadFileToS3(file) {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error(`Format non supporté : ${file.name}`);
  }
  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
    throw new Error(`${file.name} dépasse ${MAX_UPLOAD_MB} Mo`);
  }
  const { data } = await api.post("/admin/uploads/presign", {
    content_type: file.type,
    size: file.size,
  });
  await axios.put(data.upload_url, file, {
    headers: { "Content-Type": file.type },
    transformRequest: [(d) => d],
  });
  return data.public_url;
}

function ImageUploader({ images, onChange }) {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [manualUrl, setManualUrl] = useState("");

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of list) {
      try {
        const url = await uploadFileToS3(f);
        uploaded.push(url);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message || "Échec upload");
      }
    }
    setUploading(false);
    if (uploaded.length > 0) {
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} ajoutée${uploaded.length > 1 ? "s" : ""}`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => onChange(images.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    onChange(next);
  };

  const addManual = () => {
    const u = toDriveDirect(manualUrl.trim());
    if (!u) return;
    onChange([...images, u]);
    setManualUrl("");
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed cursor-pointer text-center py-8 px-4 transition-colors ${
          dragging ? "border-berulia-rosedeep bg-berulia-rose/20" : "border-berulia-border bg-berulia-cream/30 hover:bg-berulia-cream/60"
        }`}
        data-testid="image-dropzone"
      >
        <p className="overline text-berulia-inkmuted">
          {uploading ? "Envoi en cours…" : "Glissez-déposez vos photos ici"}
        </p>
        <p className="text-xs text-berulia-inkmuted italic mt-1">
          ou cliquez pour parcourir — JPG, PNG, WebP, AVIF, GIF — max {MAX_UPLOAD_MB} Mo
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          multiple
          hidden
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="image-file-input"
        />
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {images.map((u, i) => (
            <div key={`${u}-${i}`} className="relative group border border-berulia-border bg-berulia-beigewarm">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={toDriveDirect(u)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              {i === 0 ? (
                <span className="absolute top-1 left-1 overline bg-berulia-ink text-berulia-beige px-1.5 py-0.5 text-[9px] flex items-center gap-1">
                  ★ Principale
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setPrimary(i)}
                  data-testid={`set-primary-${i}`}
                  title="Définir comme photo principale"
                  className="absolute top-1 left-1 bg-white/90 hover:bg-berulia-rosedeep hover:text-white text-berulia-ink text-[11px] px-1.5 py-0.5 transition opacity-0 group-hover:opacity-100"
                >
                  ★ Principale
                </button>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => moveUp(i)} disabled={i === 0} title="Remonter d'un cran" className="text-white text-xs px-2 py-1 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => removeAt(i)} title="Supprimer" className="text-white text-xs px-2 py-1">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="url"
          placeholder="ou collez une URL (Google Drive accepté)"
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
          className="flex-1 border border-berulia-border px-3 py-2 text-sm"
        />
        <button type="button" onClick={addManual} className="overline px-4 py-2 border border-berulia-border">
          Ajouter
        </button>
      </div>
    </div>
  );
}

// Build a flat select list with hierarchy. Parents are shown as disabled labels.
function buildCategoryOptions(cats) {
  const tops = cats.filter((c) => !c.parent_slug).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const options = [];
  for (const t of tops) {
    const children = cats.filter((c) => c.parent_slug === t.slug).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    if (children.length > 0) {
      // Parent header (disabled) + children indented
      options.push({ label: t.name, group: true });
      for (const c of children) options.push({ label: `${t.name} › ${c.name}`, value: c.slug });
    } else {
      // Leaf parent (selectable on its own)
      options.push({ label: t.name, value: t.slug });
    }
  }
  return options;
}

function ProductForm({ initial, cats, products, onSave, onCancel }) {
  const [form, setForm] = useState(initial || empty);
  const [sizeStr, setSizeStr] = useState((form.sizes || []).join(","));
  const [colorStr, setColorStr] = useState((form.colors || []).join(","));
  const [relatedSearch, setRelatedSearch] = useState("");

  const catOptions = useMemo(() => buildCategoryOptions(cats), [cats]);

  const relatedIds = form.related_product_ids || [];
  const productsById = useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  const relatedCandidates = useMemo(() => {
    const q = relatedSearch.trim().toLowerCase();
    return (products || [])
      .filter((p) => p.id !== form.code && p.id !== initial?.id)
      .filter((p) => !relatedIds.includes(p.id))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [products, relatedIds, relatedSearch, initial]);

  const addRelated = (id) => {
    if (relatedIds.length >= MAX_RELATED) return;
    setForm({ ...form, related_product_ids: [...relatedIds, id] });
    setRelatedSearch("");
  };
  const removeRelated = (id) => {
    setForm({ ...form, related_product_ids: relatedIds.filter((x) => x !== id) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_slug) {
      toast.error("Choisissez une catégorie pour ce produit.");
      return;
    }
    const sizes = sizeStr.split(",").map((s) => s.trim()).filter(Boolean);
    const colors = colorStr.split(",").map((s) => s.trim()).filter(Boolean);
    const stock_by_size = {};
    sizes.forEach((s) => { stock_by_size[s] = parseInt(form.stock_by_size[s] ?? 0, 10) || 0; });
    const images = (form.images || []).map((s) => toDriveDirect(s.trim())).filter(Boolean);
    const payload = { ...form, price: parseFloat(form.price) || 0, sale_percent: parseInt(form.sale_percent, 10) || 0, sizes, stock_by_size, colors, images };
    try {
      await onSave(payload);
      toast.success("Enregistré ✿");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-berulia-border p-6 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <label>
          <span className="overline">Code article</span>
          <input data-testid="prod-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
        </label>
        <label>
          <span className="overline">Nom</span>
          <input data-testid="prod-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
        </label>
        <label>
          <span className="overline">Prix (€)</span>
          <input data-testid="prod-price" type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
        </label>
        <label>
          <span className="overline">Remise soldes (%)</span>
          <input
            data-testid="prod-sale-percent"
            type="number"
            min="0"
            max="99"
            step="1"
            value={form.sale_percent || ""}
            onChange={(e) => {
              const v = e.target.value === "" ? 0 : Math.max(0, Math.min(99, parseInt(e.target.value, 10) || 0));
              setForm({ ...form, sale_percent: v });
            }}
            placeholder="0"
            className="mt-1 w-full border border-berulia-border px-3 py-2"
          />
          {(() => {
            const pct = parseInt(form.sale_percent, 10) || 0;
            const base = parseFloat(form.price) || 0;
            if (pct > 0 && base > 0) {
              const sale = Math.round(base * (1 - pct / 100) * 100) / 100;
              return (
                <span className="text-[11px] text-berulia-rosedeep italic block mt-1">
                  Prix soldé&nbsp;: <strong>{formatPrice(sale)}</strong>{" "}
                  <span className="text-berulia-inkmuted line-through">{formatPrice(base)}</span> — calculé automatiquement.
                </span>
              );
            }
            return (
              <span className="text-[11px] text-berulia-inkmuted italic block mt-1">
                0 = plein tarif. Sinon le prix barré s'affiche automatiquement côté boutique.
              </span>
            );
          })()}
        </label>

        <label className="md:col-span-2">
          <span className="overline">Catégorie</span>
          <select
            data-testid="prod-category"
            required
            value={form.category_slug}
            onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
            className="mt-1 w-full border border-berulia-border px-3 py-2 bg-white"
          >
            <option value="">— Choisir une catégorie —</option>
            {catOptions.map((o, i) =>
              o.group ? (
                <option key={`g-${i}`} disabled className="font-bold bg-berulia-cream">
                  {o.label}
                </option>
              ) : (
                <option key={o.value} value={o.value}>{o.label}</option>
              )
            )}
          </select>
          <span className="text-[11px] text-berulia-inkmuted italic block mt-1">
            Les groupes (ex. <em>Vêtements</em>) ne sont pas sélectionnables — choisissez une sous-catégorie.
          </span>
        </label>
      </div>

      <label className="block">
        <span className="overline">Description</span>
        <textarea data-testid="prod-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
        <span className="text-[11px] text-berulia-inkmuted italic mt-1 block">
          Les sauts de ligne sont préservés sur la fiche produit.
        </span>
      </label>

      <label className="block">
        <span className="overline">Composition</span>
        <textarea data-testid="prod-composition" rows={3} value={form.composition || ""} onChange={(e) => setForm({ ...form, composition: e.target.value })} placeholder="ex. 80% coton, 20% lin&#10;Doublure : 100% viscose" className="mt-1 w-full border border-berulia-border px-3 py-2" />
      </label>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="overline">Tailles (séparées par virgule)</span>
          <input data-testid="prod-sizes" value={sizeStr} onChange={(e) => setSizeStr(e.target.value)} placeholder="XS,S,M,L" className="mt-1 w-full border border-berulia-border px-3 py-2" />
        </label>
        <label className="block">
          <span className="overline">Couleurs (séparées par virgule)</span>
          <input data-testid="prod-colors" value={colorStr} onChange={(e) => setColorStr(e.target.value)} placeholder="Rose poudré, Beige" className="mt-1 w-full border border-berulia-border px-3 py-2" />
        </label>
      </div>

      {sizeStr && (
        <div>
          <p className="overline mb-2">Stock par taille</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {sizeStr.split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
              <label key={s} className="text-sm bg-berulia-cream/40 p-2 border border-berulia-border">
                <span className="overline">{s}</span>
                <input
                  type="number"
                  min="0"
                  value={form.stock_by_size?.[s] ?? 0}
                  onChange={(e) => setForm({ ...form, stock_by_size: { ...form.stock_by_size, [s]: parseInt(e.target.value || "0", 10) } })}
                  className="mt-1 w-full border border-berulia-border px-2 py-1"
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="block">
        <span className="overline">Photos</span>
        <div className="mt-1">
          <ImageUploader
            images={form.images || []}
            onChange={(imgs) => setForm({ ...form, images: imgs })}
          />
        </div>
      </div>

      <div className="pt-3 border-t border-berulia-border">
        <span className="overline">Produits associés (max {MAX_RELATED})</span>
        <p className="text-[11px] text-berulia-inkmuted italic mt-1">Affichés sur la fiche produit sous "À associer".</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {relatedIds.map((id) => {
            const p = productsById[id];
            return (
              <span key={id} className="inline-flex items-center gap-2 bg-berulia-rose px-3 py-1 text-xs">
                {p ? p.name : `(supprimé) ${id.slice(0, 8)}`}
                <button type="button" onClick={() => removeRelated(id)} aria-label="Retirer" className="text-berulia-rosedeep hover:text-berulia-ink">×</button>
              </span>
            );
          })}
          {relatedIds.length === 0 && <span className="text-xs text-berulia-inkmuted italic">Aucun produit associé.</span>}
        </div>
        {relatedIds.length < MAX_RELATED && (
          <div className="mt-3 relative">
            <input
              type="text"
              value={relatedSearch}
              onChange={(e) => setRelatedSearch(e.target.value)}
              placeholder="Rechercher un produit par nom ou code…"
              className="w-full border border-berulia-border px-3 py-2 text-sm"
              data-testid="related-search"
            />
            {relatedSearch && relatedCandidates.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto border border-berulia-border bg-white shadow-sm">
                {relatedCandidates.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => addRelated(p.id)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-berulia-beigewarm flex items-center gap-3"
                    >
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-8 h-8 object-cover" />}
                      <span className="flex-1">{p.name}</span>
                      <span className="text-xs text-berulia-inkmuted">{p.code}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-4 text-sm pt-2 border-t border-berulia-border">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked, is_favorite: e.target.checked ? false : form.is_favorite })} />
          Nouveau
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_favorite} onChange={(e) => setForm({ ...form, is_favorite: e.target.checked, is_new: e.target.checked ? false : form.is_new })} />
          Coup de cœur
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Actif (visible côté boutique)
        </label>
        <span className="text-[11px] text-berulia-inkmuted italic">Un produit ne peut pas être à la fois Nouveau et Coup de cœur.</span>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="overline px-5 py-2 border border-berulia-border">Annuler</button>
        <button type="submit" data-testid="prod-save" className="bg-berulia-ink text-berulia-beige overline px-6 py-2">Enregistrer</button>
      </div>
    </form>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const save = async (payload) => {
    if (editing && editing.id) await api.put(`/admin/products/${editing.id}`, payload);
    else await api.post("/admin/products", payload);
    setEditing(null);
    load();
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  // Map slug → display name with parent prefix
  const catLabel = useMemo(() => {
    const map = new Map();
    for (const c of cats) {
      const parent = c.parent_slug ? cats.find((p) => p.slug === c.parent_slug) : null;
      map.set(c.slug, parent ? `${parent.name} › ${c.name}` : c.name);
    }
    return map;
  }, [cats]);

  // Filter / search
  const filtered = useMemo(() => {
    let list = products;
    if (filterCat) {
      // Filter by category OR any of its descendants
      const descendants = cats.filter((c) => c.slug === filterCat || c.parent_slug === filterCat).map((c) => c.slug);
      list = list.filter((p) => descendants.includes(p.category_slug));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q));
    }
    return list;
  }, [products, filterCat, search, cats]);

  const filterOptions = useMemo(() => buildCategoryOptions(cats), [cats]);

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-berulia-rosedeep">BERULIA</p>
          <h1 className="font-heading text-3xl sm:text-4xl mt-2">Produits</h1>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="new-product-btn" className="bg-berulia-ink text-berulia-beige overline px-6 py-3">
          + Nouveau produit
        </button>
      </div>

      {editing && (
        <div className="mb-8">
          <ProductForm initial={editing} cats={cats} products={products} onSave={save} onCancel={() => setEditing(null)} />
        </div>
      )}

      {/* Filters bar */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="border border-berulia-border bg-white px-3 py-2 text-sm"
          data-testid="filter-cat"
        >
          <option value="">Toutes les catégories</option>
          {filterOptions.map((o, i) =>
            o.group ? (
              <option key={`g-${i}`} value={o.label.toLowerCase()} disabled className="font-bold">{o.label}</option>
            ) : (
              <option key={o.value} value={o.value}>{o.label}</option>
            )
          )}
        </select>
        <input
          type="search"
          placeholder="Rechercher (nom ou code)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-berulia-border bg-white px-3 py-2 text-sm flex-1 min-w-[200px]"
          data-testid="filter-search"
        />
        <span className="text-sm text-berulia-inkmuted">{filtered.length} / {products.length} produit{products.length > 1 ? "s" : ""}</span>
      </div>

      <div className="bg-white border border-berulia-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-berulia-beigewarm">
            <tr>
              <th className="text-left p-3">Photo</th>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Catégorie</th>
              <th className="text-right p-3">Prix</th>
              <th className="text-right p-3">Stock</th>
              <th className="text-left p-3">Statuts</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const stock = Object.values(p.stock_by_size || {}).reduce((a, b) => a + b, 0);
              const label = catLabel.get(p.category_slug) || p.category_slug;
              const orphan = !catLabel.has(p.category_slug);
              return (
                <tr key={p.id} className="border-t border-berulia-border" data-testid={`row-${p.code}`}>
                  <td className="p-3"><img src={p.images?.[0]} alt="" className="w-10 h-14 object-cover" onError={(e) => { e.target.style.visibility = "hidden"; }} /></td>
                  <td className="p-3 overline text-berulia-inkmuted">{p.code}</td>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3">
                    <span className={orphan ? "text-red-700/70 italic" : ""}>{label}</span>
                    {orphan && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-700/60">orphelin</span>}
                  </td>
                  <td className="p-3 text-right">
                    {p.sale_percent > 0 ? (
                      <span className="whitespace-nowrap">
                        <span className="text-berulia-rosedeep font-medium">{formatPrice((Math.round(p.price * (1 - p.sale_percent / 100) * 100) / 100))}</span>{" "}
                        <span className="text-berulia-inkmuted line-through text-xs">{formatPrice(p.price)}</span>
                      </span>
                    ) : (
                      <>{formatPrice(p.price)}</>
                    )}
                  </td>
                  <td className={`p-3 text-right ${stock === 0 ? "text-red-700/70" : ""}`}>{stock}</td>
                  <td className="p-3 space-x-1">
                    {p.is_new && <span className="overline bg-berulia-beigewarm px-2 py-1">N</span>}
                    {p.is_favorite && <span className="overline bg-berulia-rose px-2 py-1">♥</span>}
                    {p.sale_percent > 0 && <span className="overline bg-berulia-rosedeep text-white px-2 py-1">−{p.sale_percent}%</span>}
                    {!p.is_active && <span className="overline bg-berulia-inkmuted text-berulia-beige px-2 py-1">Off</span>}
                  </td>
                  <td className="p-3 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => setEditing(p)} data-testid={`edit-${p.code}`} className="overline link-underline">Éditer</button>
                    <button onClick={() => remove(p.id, p.name)} className="overline text-red-700/70">Supprimer</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-berulia-inkmuted">Aucun produit. {products.length > 0 ? "Essayez d'élargir vos filtres." : "Créez-en un pour commencer."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
